import { freePlanFeatures, mainUserPlans, subUserPlans } from 'pricing';
import { useLanguageContext } from 'contexts/Localization';
import useTranslation from 'customHooks/translate';
import { useAnimateOnView } from 'customHooks/useAnimateOnView';
import { useNavigate } from 'react-router-dom';

import { fadeInUp } from './landing-motion';

const Pricing = () => {
  const translation = useTranslation();
  // @ts-ignore
  const { language } = useLanguageContext();
  const navigate = useNavigate();
  const { ref, visible } = useAnimateOnView();

  return (
    <section
      ref={ref}
      id="pricing"
      className="mt-[48px] flex flex-col gap-[32px] px-[16px] md:mt-[72px] md:px-[78px] lg:px-[156px]"
    >
      <div
        className={`flex max-w-[700px] flex-col gap-[16px] md:text-left ${
          visible ? fadeInUp : 'opacity-0'
        }`}
      >
        <p className="text-[32px] font-bold tracking-tight text-eerie-black md:text-[40px] md:leading-tight">
          {translation.home.Header.Pricing}
        </p>
        <p className="text-[18px] font-normal leading-normal text-[rgba(41,45,50,1)] opacity-70">
          {translation.home.Pricing.Description}
        </p>
        <p className="inline-flex w-fit items-center rounded-full bg-[rgba(70,106,251,0.12)] px-[14px] py-[8px] text-[18px] font-semibold text-[rgba(70,106,251,1)]">
          {translation.home.Pricing.MarketingHook}
        </p>
      </div>
      <div>
        <div
          className={`flex flex-wrap items-center justify-between gap-[20px] p-[16px] md:p-[32px] ${
            visible ? fadeInUp : 'opacity-0'
          }`}
          style={{
            border: '1px solid rgba(41, 45, 50, 0.1)',
            animationDelay: visible ? '0.08s' : undefined,
          }}
        >
          <div className="flex flex-col">
            <p className="text-[20px] font-bold text-[rgba(70,106,251,1)]">
              {translation.home.Pricing.ThreeMonthFree}
            </p>
            <p className="text-[32px] font-bold text-[rgba(41,45,50,1)] md:text-[40px]">
              {translation.home.Pricing.CompletelyFree}
            </p>
          </div>
          <ul className="space-y-3 md:space-y-4 [&>*]:text-charleston-green">
            {freePlanFeatures[language]?.map((feature, index) => (
              <li key={index} className="flex items-center gap-[4px]">
                <img
                  src="assets/tick-circle.svg"
                  alt={feature}
                  className="h-4 w-4 shrink-0"
                />
                <span className="text-[14px] font-medium leading-relaxed text-[rgba(41,45,50,1)]">
                  {feature}
                </span>
              </li>
            ))}
          </ul>
          <button
            onClick={() => navigate('/register')}
            className="w-full rounded-[34px] bg-[rgba(70,106,251,1)] py-[10px] text-[16px] font-bold text-white transition-all duration-300 hover:-translate-y-[2px] hover:bg-[rgba(70,106,251,0.95)] hover:shadow-[0_10px_24px_rgba(70,106,251,0.24)] active:translate-y-0 md:max-w-[280px] md:py-[14px]"
          >
            {translation.home.Pricing.ChoosePlan}
          </button>
        </div>
        <div className="grid grid-cols-1 overflow-hidden border border-r-0 border-t-0 border-[rgba(27,27,31,0.12)] md:grid-cols-3 md:border-r">
          {mainUserPlans?.map((mainUserPlan, planIndex) => (
            <div
              key={mainUserPlan.id}
              className={`relative flex flex-col gap-5 border-b border-r border-t-0 border-[rgba(27,27,31,0.12)] bg-white p-[16px] transition-all duration-200 last:border-b-0  md:gap-6 md:border-b-0 md:p-[32px] md:[&:nth-child(3n)]:border-r-0 ${
                visible ? fadeInUp : 'opacity-0'
              }`}
              style={{
                animationDelay: visible
                  ? `${0.12 + planIndex * 0.06}s`
                  : undefined,
              }}
            >
              <div className="flex flex-col flex-wrap items-baseline gap-[6px] md:gap-[12px]">
                <h4 className="text-lg font-bold tracking-tight text-ultramarine-blue md:text-xl">
                  {mainUserPlan?.duration} {translation.reports.Months}
                </h4>
                <div className="flex flex-wrap items-center gap-[12px]">
                  <span className="text-2xl font-bold tabular-nums text-eerie-black md:text-3xl">
                    {mainUserPlan.price} {mainUserPlan.currency}
                  </span>
                  {mainUserPlan.recommended && (
                    <div className="flex justify-center">
                      <span className="inline-flex items-center rounded-full bg-[rgba(109,205,144,1)] px-3 py-1 text-xs font-semibold tracking-wide text-white shadow-sm">
                        {translation.home.Pricing.Recommended}
                      </span>
                    </div>
                  )}
                </div>
                <span className="text-sm font-semibold text-charleston-green/80 md:text-base">
                  {mainUserPlan.pricePerMonth} {mainUserPlan.currency} /{' '}
                  {translation.reports.Month}
                </span>
              </div>
              <div className="h-[1px] w-full bg-[rgba(41,45,50,0.1)] px-[21px]" />
              <ul className="space-y-3">
                {mainUserPlan?.features[language]?.map((feature, index) => (
                  <li key={index} className="flex items-center gap-[4px]">
                    <img
                      src="assets/tick-circle.svg"
                      alt={feature}
                      className="h-4 w-4 shrink-0"
                    />
                    <span className="text-sm font-medium leading-relaxed text-charleston-green/90 md:text-[15px]">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/register')}
                className="mt-auto w-full rounded-[34px] bg-[rgba(70,106,251,1)] py-[10px] text-[16px] font-bold text-white transition-all duration-300 hover:-translate-y-[2px] hover:bg-[rgba(70,106,251,0.95)] hover:shadow-[0_10px_24px_rgba(70,106,251,0.24)] active:translate-y-0 md:py-[14px]"
              >
                {translation.home.Pricing.ChoosePlan}
              </button>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 overflow-hidden border border-r-0 border-t-0 border-[rgba(27,27,31,0.12)] md:grid-cols-3 md:border-r">
          {subUserPlans?.map((subUserPlan, planIndex) => (
            <div
              key={subUserPlan.id}
              className={`relative flex flex-col gap-5 border-b border-r border-t-0 border-[rgba(27,27,31,0.12)] bg-white p-[16px] transition-all duration-200 last:border-b-0 md:gap-6 md:border-b-0  md:p-[32px] md:[&:nth-child(3n)]:border-r-0 ${
                visible ? fadeInUp : 'opacity-0'
              }`}
              style={{
                animationDelay: visible
                  ? `${0.28 + planIndex * 0.06}s`
                  : undefined,
              }}
            >
              <div className="flex flex-col flex-wrap items-baseline gap-[6px] md:gap-[12px]">
                <h4 className="text-lg font-bold tracking-tight text-ultramarine-blue md:text-xl">
                  {subUserPlan?.duration} {translation.reports.Months}
                </h4>
                <div className="flex flex-wrap items-center gap-[12px]">
                  <span className="text-2xl font-bold tabular-nums text-eerie-black md:text-3xl">
                    {subUserPlan.price} {subUserPlan.currency}
                  </span>
                  {subUserPlan.recommended && (
                    <div className="flex justify-center">
                      <span className="inline-flex items-center rounded-full bg-[rgba(109,205,144,1)] px-3 py-1 text-xs font-semibold tracking-wide text-white shadow-sm">
                        {translation.home.Pricing.Recommended}
                      </span>
                    </div>
                  )}
                </div>
                <span className="text-[16px] font-medium text-[rgba(41,45,50,1)] opacity-70">
                  {translation.home.Pricing.ForSubUser}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
